import React, { useState } from 'react';
import { Users, Mail, Trash2, Shield, UserPlus, X, Info } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Loading } from '../components/ui/Loading';
import { EmptyState } from '../components/ui/EmptyState';
import { formatDate } from '../lib/utils';
import {
    useTeamMembers,
    useTeamInvitations,
    useInviteTeamMember,
    useCancelInvitation,
    useRemoveTeamMember,
    useUpdateMemberRole,
} from '../hooks/useTeam';
import { useOrganization } from '../hooks/useOrganization';
import { TeamRole } from '../types';

const Team: React.FC = () => {
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<TeamRole>('developer');

    const { data: members, isLoading: membersLoading } = useTeamMembers();
    const { data: invitations, isLoading: invitationsLoading } = useTeamInvitations();
    const { data: organization } = useOrganization();
    const inviteMutation = useInviteTeamMember();
    const cancelMutation = useCancelInvitation();
    const removeMutation = useRemoveTeamMember();
    const updateRoleMutation = useUpdateMemberRole();

    const handleInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await inviteMutation.mutateAsync({ email: inviteEmail, role: inviteRole });
            setShowInviteModal(false);
            setInviteEmail('');
            setInviteRole('developer');
        } catch (error) {
            // Error is already handled by mutation onError
            // Just keep the modal open so user can see the error toast
        }
    };

    const handleRemoveMember = (memberId: number, memberName: string) => {
        if (window.confirm(`Are you sure you want to remove ${memberName} from your team?`)) {
            removeMutation.mutate(memberId);
        }
    };

    const handleUpdateRole = (memberId: number, currentRole: TeamRole) => {
        const newRole = prompt('Enter new role (owner, admin, developer, viewer):', currentRole);
        if (newRole && ['owner', 'admin', 'developer', 'viewer'].includes(newRole)) {
            updateRoleMutation.mutate({ memberId, role: newRole as TeamRole });
        }
    };

    if (membersLoading || invitationsLoading) {
        return <Loading fullScreen message="Loading team..." />;
    }

    const getRoleBadgeColor = (role: TeamRole): string => {
        switch (role) {
            case 'owner':
                return 'text-purple-700 bg-purple-100';
            case 'admin':
                return 'text-blue-700 bg-blue-100';
            case 'developer':
                return 'text-green-700 bg-green-100';
            case 'viewer':
                return 'text-gray-700 bg-gray-100';
            default:
                return 'text-gray-700 bg-gray-100';
        }
    };

    const currentTeamSize = (members?.length || 0) + (invitations?.length || 0);
    const teamLimit = organization?.limits?.team_members || 1;
    const canAddMore = currentTeamSize < teamLimit;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
                    <p className="text-gray-600 mt-1">Manage your team members and invitations</p>
                    {organization?.limits && (
                        <p className="text-sm text-gray-500 mt-1">
                            {currentTeamSize} / {teamLimit} team member{teamLimit !== 1 ? 's' : ''}
                        </p>
                    )}
                </div>
                <button
                    onClick={() => setShowInviteModal(true)}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!canAddMore}
                    title={!canAddMore ? 'Team limit reached' : 'Invite team member'}
                >
                    <UserPlus className="w-5 h-5" />
                    Invite Member
                </button>
            </div>

            {/* Team Limit Warning */}
            {!canAddMore && organization?.upgrade && (
                <Card className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200">
                    <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                            <Info className="w-5 h-5 text-orange-600" />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-sm font-semibold text-orange-900 mb-1">
                                Team Member Limit Reached
                            </h3>
                            <p className="text-sm text-orange-800 mb-2">
                                Your current plan allows up to <strong>{teamLimit} team member{teamLimit !== 1 ? 's' : ''}</strong>. 
                                You have {currentTeamSize} member{currentTeamSize !== 1 ? 's' : ''} (including pending invitations).
                            </p>
                            {organization.upgrade.available && (
                                <>
                                    <p className="text-sm text-orange-700 mb-3">
                                        Upgrade to add more team members and unlock additional features.
                                    </p>
                                    <a
                                        href="/pricing"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                                    >
                                        View Pricing Plans
                                    </a>
                                </>
                            )}
                        </div>
                    </div>
                </Card>
            )}

            {/* Team Members */}
            <Card>
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
                    <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-600">{members?.length || 0} members</span>
                    </div>
                </div>

                {!members || members.length === 0 ? (
                    <EmptyState
                        icon={Users}
                        title="No team members yet"
                        description="Invite your first team member to get started"
                    />
                ) : (
                    <div className="space-y-4">
                        {members.map((member) => (
                            <div
                                key={member.id}
                                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                        <Users className="w-5 h-5 text-primary-600" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold text-gray-900">{member.name}</h3>
                                            <Badge className={getRoleBadgeColor(member.role)}>
                                                {member.role}
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-gray-600">{member.email}</p>
                                        <p className="text-xs text-gray-400 mt-1">
                                            Joined {formatDate(member.created_at)}
                                        </p>
                                    </div>
                                </div>
                                {member.role !== 'owner' && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleUpdateRole(member.id, member.role)}
                                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                                            title="Change Role"
                                        >
                                            <Shield className="w-4 h-4 text-blue-600" />
                                        </button>
                                        <button
                                            onClick={() => handleRemoveMember(member.id, member.name)}
                                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Remove Member"
                                        >
                                            <Trash2 className="w-4 h-4 text-red-600" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Pending Invitations */}
            <Card>
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Pending Invitations</h2>
                    <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-gray-400" />
                        <span className="text-sm text-gray-600">{invitations?.length || 0} pending</span>
                    </div>
                </div>

                {!invitations || invitations.length === 0 ? (
                    <EmptyState
                        icon={Mail}
                        title="No pending invitations"
                        description="All invitations have been accepted or expired"
                    />
                ) : (
                    <div className="space-y-4">
                        {invitations.map((invitation) => (
                            <div
                                key={invitation.id}
                                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
                            >
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold text-gray-900">{invitation.email}</h3>
                                        <Badge className={getRoleBadgeColor(invitation.role)}>
                                            {invitation.role}
                                        </Badge>
                                    </div>
                                    <p className="text-sm text-gray-600">Invited by {invitation.invited_by}</p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Expires {formatDate(invitation.expires_at)}
                                    </p>
                                </div>
                                <button
                                    onClick={() => cancelMutation.mutate(invitation.id)}
                                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Cancel Invitation"
                                >
                                    <X className="w-4 h-4 text-red-600" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <Card className="max-w-md w-full">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">Invite Team Member</h2>
                            <button
                                onClick={() => setShowInviteModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleInvite} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                                <input
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="colleague@company.com"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                                <select
                                    value={inviteRole}
                                    onChange={(e) => setInviteRole(e.target.value as TeamRole)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <option value="viewer">Viewer - Can view data only</option>
                                    <option value="developer">Developer - Can manage API keys and integrations</option>
                                    <option value="admin">Admin - Full access except billing</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowInviteModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={inviteMutation.isPending}
                                    className="flex-1 btn-primary disabled:opacity-50"
                                >
                                    {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
                                </button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default Team;